import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '@/services/auth.service';
import { apiService } from '@/services/api.service';
import { useAuthStore, StaffRole, Permission } from '@/stores/auth.store';
import styles from './AuthCallback.module.scss';

/**
 * Map LuckPerms group name to StaffRole enum
 */
function mapGroupToRole(group: string): StaffRole {
  switch (group.toLowerCase()) {
    case 'admin':
    case 'administrateur':
      return StaffRole.ADMIN;
    case 'moderator':
    case 'moderateur':
    case 'mod':
      return StaffRole.MODERATOR;
    case 'builder':
    case 'build':
      return StaffRole.BUILDER;
    default:
      return StaffRole.HELPER;
  }
}

type Status = 'loading' | 'verifying' | 'success' | 'error';

export function AuthCallback() {
  const [status, setStatus] = useState<Status>('loading');
  const [statusMessage, setStatusMessage] = useState<string>('Validation de votre compte Xbox');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);

  // Prevent double execution in StrictMode
  const isProcessing = useRef(false);

  useEffect(() => {
    const processCallback = async () => {
      // Prevent double call (React StrictMode mounts twice in dev)
      if (isProcessing.current) {
        return;
      }
      isProcessing.current = true;

      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Handle OAuth errors
      if (error) {
        setStatus('error');
        setErrorMessage(errorDescription || 'Authentification annulee');
        authService.setAuthPending(false);
        return;
      }

      // No code provided
      if (!code) {
        setStatus('error');
        setErrorMessage('Code d\'authentification manquant');
        authService.setAuthPending(false);
        return;
      }

      try {
        // Step 1: Validate code with login.paladium.games
        setStatusMessage('Validation du code Microsoft...');
        const validateResult = await authService.validateCode(code);

        if (!validateResult.success || !validateResult.token) {
          setStatus('error');
          setErrorMessage(validateResult.message || 'Echec de la validation');
          authService.setAuthPending(false);
          return;
        }

        const token = validateResult.token;

        // Store token
        authService.setToken(token);

        // Decode token to get basic info
        const tokenPayload = authService.decodeToken(token);
        if (!tokenPayload) {
          setStatus('error');
          setErrorMessage('Token invalide');
          authService.setAuthPending(false);
          return;
        }

        console.log('Token payload:', tokenPayload); // Debug log

        // Step 2: Verify panel access permission
        setStatus('verifying');
        setStatusMessage('Verification des permissions...');

        const accessResult = await authService.checkPanelAccess();

        console.log('Access check result:', accessResult); // Debug log

        if (!accessResult.hasAccess) {
          // User doesn't have panel access permission
          // Still store auth so they can see AccessDenied page
          setAuth(
            {
              id: tokenPayload.xuid,
              xuid: tokenPayload.xuid,
              gamertag: tokenPayload.gamertag,
              role: StaffRole.HELPER,
              permissions: [], // No permissions
            },
            token,
            ''
          );

          authService.setAuthPending(false);

          // Redirect to access denied page (full page reload for CF Pages routing)
          window.location.href = '/access-denied';
          return;
        }

        // User has access - fetch full user info from Quarkus
        setStatusMessage('Recuperation des informations...');
        const userInfoResult = await apiService.getCurrentUser();

        console.log('User info result:', userInfoResult); // Debug log

        authService.setAuthPending(false);

        if (userInfoResult.data) {
          const userInfo = userInfoResult.data;
          setAuth(
            {
              id: userInfo.xuid,
              xuid: userInfo.xuid,
              gamertag: userInfo.gamertag,
              role: userInfo.isAdmin ? StaffRole.ADMIN : mapGroupToRole(userInfo.rank),
              displayRole: userInfo.displayName,
              permissions: (userInfo.permissions ?? []) as Permission[],
              isAdmin: !!userInfo.isAdmin,
              gradeIds: userInfo.gradeIds ?? [],
              panelPermissions: userInfo.panelPermissions ?? [],
              manageAccess: !!userInfo.manageAccess,
              canAssignGrades: !!userInfo.canAssignGrades,
              assignableGradeIds: userInfo.assignableGradeIds ?? [],
              adminProjects: userInfo.adminProjects ?? [],
              gameGroups: userInfo.gameGroups ?? {},
            },
            token,
            ''
          );
        } else {
          // Fallback to basic info from token
          setAuth(
            {
              id: tokenPayload.xuid,
              xuid: tokenPayload.xuid,
              gamertag: tokenPayload.gamertag,
              role: StaffRole.HELPER,
              permissions: [Permission.VIEW_DASHBOARD],
            },
            token,
            ''
          );
        }

        setStatus('success');
        setStatusMessage('Redirection vers le panel...');

        // Accueil après un court délai (rechargement complet pour le routage CF Pages) : le
        // Dashboard, ou la première page servie par le satellite du groupe de jeu actif.
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);

      } catch (err) {
        console.error('Auth callback error:', err);
        setStatus('error');
        setErrorMessage('Erreur lors de la connexion');
        authService.setAuthPending(false);
      }
    };

    processCallback();
  }, [searchParams, navigate, setAuth]);

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {(status === 'loading' || status === 'verifying') && (
          <>
            <div className={styles.spinner} />
            <h2 className={styles.title}>Connexion en cours...</h2>
            <p className={styles.subtitle}>{statusMessage}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className={styles.successIcon}>✓</div>
            <h2 className={styles.title}>Connexion reussie !</h2>
            <p className={styles.subtitle}>{statusMessage}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className={styles.errorIcon}>✕</div>
            <h2 className={styles.title}>Echec de la connexion</h2>
            <p className={styles.subtitle}>{errorMessage}</p>
            <button
              className={styles.retryButton}
              onClick={() => navigate('/login', { replace: true })}
            >
              Reessayer
            </button>
          </>
        )}
      </div>
    </div>
  );
}
